import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/planner/lib/apiClient";
import { useAuth } from "@/planner/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { catColor } from "@/planner/lib/vendy";
import { toast } from "sonner";
import { Route as RouteIcon, MapPin, ShoppingBasket, ChevronRight, AlertTriangle, CheckCircle2, Navigation, Printer } from "lucide-react";

function numIcon(n, done) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${done ? "#16a34a" : "#0E7490"};color:#fff;width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-family:'Space Grotesk',sans-serif;box-shadow:0 2px 6px rgba(0,0,0,.3);border:2px solid #fff">${n}</div>`,
    iconSize: [30, 30], iconAnchor: [15, 15],
  });
}
function startIcon() {
  return L.divIcon({
    className: "",
    html: `<div style="background:#D97706;width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,.3);border:2px solid #fff"></div>`,
    iconSize: [26, 26], iconAnchor: [13, 26],
  });
}

function RouteMap({ start, stops }) {
  const ref = useRef();
  const mapRef = useRef();
  useEffect(() => {
    if (!ref.current) return;
    if (!mapRef.current) {
      mapRef.current = L.map(ref.current, { scrollWheelZoom: false });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "\u00a9 OpenStreetMap", maxZoom: 19,
      }).addTo(mapRef.current);
    }
    const map = mapRef.current;
    map.eachLayer((l) => { if (!(l instanceof L.TileLayer)) map.removeLayer(l); });
    const pts = [];
    if (start) { L.marker([start.lat, start.lon], { icon: startIcon() }).addTo(map).bindPopup("Start"); pts.push([start.lat, start.lon]); }
    stops.filter((s) => s.lat != null).forEach((s, i) => {
      const done = s.items.every((it) => it.current >= it.par);
      L.marker([s.lat, s.lon], { icon: numIcon(i + 1, done) }).addTo(map)
        .bindPopup(`${s.machine_name}${s.distance_mi != null ? ` \u2014 ${s.distance_mi} mi` : ""}`);
      pts.push([s.lat, s.lon]);
    });
    if (pts.length > 1) L.polyline(pts, { color: "#0E7490", weight: 3, opacity: 0.7, dashArray: "6" }).addTo(map);
    if (pts.length) map.fitBounds(pts, { padding: [40, 40], maxZoom: 12 });
    else map.setView([39.1, -84.5], 9);
    setTimeout(() => map.invalidateSize(), 200);
  }, [start, stops]);
  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);
  return <div ref={ref} data-testid="route-map" style={{ height: 380 }} className="rounded-lg overflow-hidden border" />;
}

export default function RoutePlanner() {
  const { canEdit } = useAuth();
  const [data, setData] = useState(null);
  const [start, setStart] = useState(null);
  const [startZip, setStartZip] = useState("");
  const [mapMode, setMapMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/inventory/route");
    setData(data); setStart(null); setMapMode(false);
  };
  useEffect(() => { load(); }, []);

  const planMap = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/inventory/route/map", { start_zip: startZip });
      setData(d); setStart(d.start); setMapMode(true);
      if (startZip && !d.start) toast.error("Couldn't locate that ZIP \u2014 showing unordered stops");
      else if (d.ordered_by_distance) toast.success("Stops ordered by distance");
    } catch (e) { toast.error("Map planning failed"); }
    finally { setBusy(false); }
  };

  const setSlot = async (stopIdx, itemIdx, newCurrent) => {
    const stop = data.stops[stopIdx];
    const item = stop.items[itemIdx];
    try {
      await api.put(`/machines/${stop.machine_id}/stock`, { slot_id: item.slot_id, current: newCurrent });
      const next = JSON.parse(JSON.stringify(data));
      next.stops[stopIdx].items[itemIdx].current = newCurrent;
      setData(next);
      if (newCurrent >= item.par) toast.success(`Stocked ${item.slot_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Stock update failed"); }
  };

  const createList = async () => {
    if (!data?.shopping_items?.length) return toast.error("Nothing to shop \u2014 all machines are stocked");
    setCreating(true);
    try {
      const items = data.shopping_items.map((s) => ({ product_id: s.product_id, name: s.name, qty: s.qty, checked: false }));
      await api.post("/shopping-lists", { name: `Route ${new Date().toLocaleDateString()}`, items });
      toast.success("Shopping list created from route");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  // Printable driver sheet: what to carry (aggregated) + per-stop load counts.
  const printRouteSheet = () => {
    if (!data?.stops?.length) { toast.error("Nothing to restock right now"); return; }
    const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const carry = (data.shopping_items || []).map((s) => `<tr><td>${esc(s.name)}</td><td class="num">${s.qty}</td></tr>`).join("")
      || `<tr><td colspan="2" class="muted">Nothing to carry.</td></tr>`;
    const stopsHtml = data.stops.map((s, i) => {
      const rows = s.items.map((it) => {
        const load = Math.max(0, (it.par || 0) - (it.current || 0));
        const cell = load > 0 ? `<span class="load">${load}</span>` : `<span class="full">full</span>`;
        return `<tr><td class="sid">${esc(it.slot_id)}</td><td>${esc(it.product_name)}</td><td class="num">${it.current || 0} / ${it.par || 0}</td><td class="num">${cell}</td></tr>`;
      }).join("");
      const stopUnits = s.items.reduce((a, it) => a + Math.max(0, (it.par || 0) - (it.current || 0)), 0);
      return `<div class="stop">
        <div class="shead"><span class="snum">${i + 1}</span><div><div class="sname">${esc(s.machine_name)}</div>
        <div class="sloc">${esc(s.location || "No location")}${s.zip_code ? ` \u00b7 ${esc(s.zip_code)}` : ""}${s.distance_mi != null ? ` \u00b7 ${s.distance_mi} mi` : ""}</div></div>
        <span class="sunits">load ${stopUnits}</span></div>
        <table class="items"><thead><tr><th>Slot</th><th>Product</th><th class="num">On&nbsp;hand</th><th class="num">Load</th></tr></thead><tbody>${rows}</tbody></table>
      </div>`;
    }).join("");
    const doc = `<!doctype html><html><head><title>Restock Route Sheet</title><style>
      *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;margin:22px;color:#111}
      h1{font-size:20px;margin:0 0 2px} .sub{color:#555;font-size:12px;margin:0 0 14px}
      h2{font-size:14px;margin:18px 0 6px;border-bottom:2px solid #222;padding-bottom:3px}
      table{border-collapse:collapse;width:100%;font-size:12px} th,td{border:1px solid #bbb;padding:5px 7px;text-align:left}
      th{background:#f2f2f2} .num{text-align:right;font-variant-numeric:tabular-nums} .muted{color:#999}
      .carry td:first-child{font-weight:600}
      .stop{border:1px solid #ccc;border-radius:6px;padding:10px;margin-bottom:12px;break-inside:avoid}
      .shead{display:flex;align-items:center;gap:10px;margin-bottom:8px}
      .snum{background:#0E7490;color:#fff;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;flex:0 0 26px}
      .sname{font-weight:700;font-size:14px} .sloc{font-size:11px;color:#666}
      .sunits{margin-left:auto;background:#b45309;color:#fff;border-radius:4px;padding:2px 8px;font-weight:700;font-size:12px}
      .items .sid{font-family:monospace} .load{background:#b45309;color:#fff;border-radius:3px;padding:1px 6px;font-weight:700} .full{color:#15803d;font-weight:600}
      @media print{body{margin:12mm}}
    </style></head><body>
      <h1>Restock Route Sheet</h1>
      <p class="sub">${new Date().toLocaleDateString()} \u00b7 ${data.totals.machines} stop${data.totals.machines !== 1 ? "s" : ""} \u00b7 ${data.totals.units} units to load${start ? ` \u00b7 from ${esc(startZip)}` : ""}</p>
      <h2>What to carry</h2>
      <table class="carry"><thead><tr><th>Product</th><th class="num">Units</th></tr></thead><tbody>${carry}</tbody></table>
      <h2>Stops</h2>
      ${stopsHtml}
      <script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script>
    </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(doc); w.document.close();
  };

  if (!data) return <div className="text-muted-foreground">Loading\u2026</div>;
  const { stops, shopping_items, totals } = data;
  const doneCount = stops.filter((s) => s.items.every((it) => it.current >= it.par)).length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><RouteIcon className="h-6 w-6 text-primary" />Stocking Route</h1>
          <p className="text-sm text-muted-foreground">Restock stops ordered by urgency, or by distance from your start. Tick off slots as you fill them.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="route-print-sheet-button" onClick={printRouteSheet} disabled={!stops.length}><Printer className="mr-2 h-4 w-4" />Print route sheet</Button>
          {canEdit && <Button data-testid="route-create-list-button" onClick={createList} disabled={creating || !shopping_items.length}><ShoppingBasket className="mr-2 h-4 w-4" />Create shopping list</Button>}
        </div>
      </div>

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs font-medium text-muted-foreground">Start ZIP (for distance ordering + map)</label>
            <Input data-testid="route-start-zip-input" className="mt-1" value={startZip} onChange={(e) => setStartZip(e.target.value)} placeholder="e.g. 45202" />
          </div>
          <Button data-testid="route-plan-map-button" onClick={planMap} disabled={busy}><Navigation className="mr-2 h-4 w-4" />{busy ? "Planning\u2026" : "Plan on map"}</Button>
        </div>
        {mapMode && stops.some((s) => s.lat != null) && (
          <div className="mt-4"><RouteMap start={start} stops={stops} /></div>
        )}
      </Card>

      <div className="grid grid-cols-3 gap-4 mb-5">
        <Card className="p-4" data-testid="route-stat-machines"><div className="text-xs uppercase text-muted-foreground">Stops</div><div className="font-heading text-2xl font-bold">{totals.machines}</div></Card>
        <Card className="p-4" data-testid="route-stat-units"><div className="text-xs uppercase text-muted-foreground">Units to restock</div><div className="font-heading text-2xl font-bold text-primary">{totals.units}</div></Card>
        <Card className="p-4" data-testid="route-stat-done"><div className="text-xs uppercase text-muted-foreground">Stops done</div><div className="font-heading text-2xl font-bold text-green-700">{doneCount}/{totals.machines}</div></Card>
      </div>

      {stops.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground"><CheckCircle2 className="mx-auto h-8 w-8 mb-2 text-green-600" />Everything is stocked. No route needed right now.</Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
          <div className="space-y-4">
            {stops.map((s, idx) => {
              const stopDone = s.items.every((it) => it.current >= it.par);
              return (
                <Card key={s.machine_id} className={`p-4 ${stopDone ? "border-green-300 bg-green-50/40" : ""}`} data-testid={`route-stop-${s.machine_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold ${stopDone ? "bg-green-600 text-white" : "bg-primary text-primary-foreground"}`}>{idx + 1}</div>
                      <div>
                        <div className="font-heading font-semibold">{s.machine_name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" />{s.location || "No location"}{s.zip_code ? ` \u00b7 ${s.zip_code}` : ""}
                          {s.distance_mi != null && <Badge variant="secondary" className="ml-1">{s.distance_mi} mi</Badge>}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {stopDone ? <Badge className="bg-green-600"><CheckCircle2 className="mr-1 h-3 w-3" />Stocked</Badge>
                        : s.empty_count > 0 && <Badge className="bg-red-600"><AlertTriangle className="mr-1 h-3 w-3" />{s.empty_count} empty</Badge>}
                    </div>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {s.items.map((it, iidx) => {
                      const c = catColor(it.category);
                      const filled = it.current >= it.par;
                      return (
                        <label key={it.slot_id} className="flex items-center gap-3 rounded-md border px-2.5 py-2 text-sm cursor-pointer" data-testid={`route-item-${s.machine_id}-${it.slot_id}`} style={{ borderLeft: `4px solid ${c.stripe}` }}>
                          <Checkbox
                            checked={filled}
                            data-testid={`route-stock-${s.machine_id}-${it.slot_id}`}
                            onCheckedChange={(v) => setSlot(idx, iidx, v ? it.par : 0)}
                          />
                          <span className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="font-mono text-xs bg-muted px-1.5 rounded">{it.slot_id}</span>
                            <span className={`truncate ${filled ? "line-through text-muted-foreground" : ""}`}>{it.product_name}</span>
                          </span>
                          <span className="font-mono text-xs whitespace-nowrap">{it.current}/{it.par}{!filled && <span className="text-red-600 font-semibold"> +{it.par - it.current}</span>}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="mt-3">
                    <Link to={`/admin/planner/machines/${s.machine_id}`}><Button variant="outline" size="sm" data-testid={`route-open-${s.machine_id}`}>Open machine <ChevronRight className="ml-1 h-4 w-4" /></Button></Link>
                  </div>
                </Card>
              );
            })}
          </div>

          <div>
            <Card className="p-4 lg:sticky lg:top-4" data-testid="route-shopping-panel">
              <div className="flex items-center gap-2 font-heading font-semibold"><ShoppingBasket className="h-4 w-4 text-primary" />Aggregated shopping list</div>
              <p className="text-xs text-muted-foreground mt-1">Everything needed across the route.</p>
              <div className="mt-3 space-y-2 max-h-[55vh] overflow-y-auto">
                {shopping_items.length === 0 ? <p className="text-sm text-muted-foreground">Nothing to buy.</p> : shopping_items.map((s) => {
                  const c = catColor(s.category);
                  return (
                    <div key={s.product_id} className="flex items-center justify-between rounded-lg border p-2.5 text-sm" style={{ borderLeft: `4px solid ${c.stripe}` }}>
                      <span>{s.name}</span><span className="font-mono font-semibold">x{s.qty}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
